import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import jwtDecode from 'jwt-decode';
import { StaffProfile } from '../../api/authApi';

export interface AuthState {
  isAuthenticated: boolean;
  accessToken: string | null;
  id: string | null;
  displayName: string | null;
  email: string | null;
  // Staff role codes, e.g. ["finance", "kyc"]
  roles: string[];
  // true after the first login with the mailed temporary password, and after a reset
  mustChangePassword: boolean;
  // ISO date-time of the token's end (8 hours); the user is logged out when it passes
  sessionExpiresAt: string | null;
  isInitialized: boolean;
  loading: boolean;
  error: string | null;
}

const loggedOutState: AuthState = {
  isAuthenticated: false,
  accessToken: null,
  id: null,
  displayName: null,
  email: null,
  roles: [],
  mustChangePassword: false,
  sessionExpiresAt: null,
  isInitialized: true,
  loading: false,
  error: null,
};

const tokenExpiry = (token: string): string | null => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    return exp ? new Date(exp * 1000).toISOString() : null;
  } catch {
    return null;
  }
};

// Before redux-persist brings back the stored profile: logged in when a live token is
// stored. The roles come with the rehydrate and are refreshed from GET /staff/me.
const getInitialAuthState = (): AuthState => {
  let token: string | null = null;
  if (typeof window !== 'undefined') {
    token = localStorage.getItem('accessToken');
  }

  if (token) {
    try {
      const decoded: any = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (!decoded.exp || decoded.exp > currentTime) {
        return {
          ...loggedOutState,
          isAuthenticated: true,
          accessToken: token,
          sessionExpiresAt: tokenExpiry(token),
        };
      }
    } catch (e) {
      // Not a JWT: dropped below
    }
    if (typeof window !== 'undefined') {
      localStorage.removeItem('accessToken');
    }
  }

  return loggedOutState;
};

const initialState: AuthState = getInitialAuthState();

const applyProfile = (state: AuthState, staff: StaffProfile) => {
  state.id = staff.id;
  state.displayName = staff.name || staff.email;
  state.email = staff.email;
  state.roles = Array.isArray(staff.roles) ? staff.roles : [];
  state.mustChangePassword = !!staff.must_change_password;
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setAuthError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    // POST /staff/login and POST /staff/change-password
    loginSuccessReducer: (
      state,
      action: PayloadAction<{ token: string; expiresAt?: string | null; staff: StaffProfile }>
    ) => {
      const { token, expiresAt, staff } = action.payload;
      state.isAuthenticated = true;
      state.accessToken = token;
      applyProfile(state, staff);
      state.sessionExpiresAt = expiresAt || tokenExpiry(token);
      state.loading = false;
      state.error = null;
    },
    // GET /staff/me
    setMeReducer: (state, action: PayloadAction<StaffProfile>) => {
      applyProfile(state, action.payload);
    },
    // 403 PASSWORD_CHANGE_REQUIRED on any /owner/* call
    setMustChangePasswordReducer: (state, action: PayloadAction<boolean>) => {
      state.mustChangePassword = action.payload;
    },
    // Not the load-time initialState: that one is logged in when a token was stored
    logoutReducer: () => loggedOutState,
    setInitializedReducer: (state) => {
      state.isInitialized = true;
    },
  },
});

export const {
  setAuthLoading,
  setAuthError,
  loginSuccessReducer,
  setMeReducer,
  setMustChangePasswordReducer,
  logoutReducer,
  setInitializedReducer,
} = authSlice.actions;

export default authSlice.reducer;
